import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { Box, Button, Card, CardContent, CardHeader, IconButton, Input, InputAdornment, Tooltip, Dialog, DialogActions, DialogTitle, FormGroup, FormControlLabel, Switch, Checkbox } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { subAdminServcie } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import ViewUserManagement from "./viewSubadmin";
import { useNavigate } from "react-router-dom";
import { EditOutlined } from "@mui/icons-material";
import { Loader } from "../../components/CustomLoader";
import CustomPagination from "../../components/CustomPagination";
import { Helmet } from "react-helmet-async";
import { capitalizeFirstLetter } from "../../utils/helpers";
import { useBulkSelect } from "../../hooks/useBulkSelect";

export default function SubAdminManagementList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);

  const {
    selectedRows,
    getCheckboxColumn,
    clearSelection,
  } = useBulkSelect(data, null, [currentPage, pageSize, status, refresh]);

  const fetchUsers = async (query) => {
    const body = {
      search: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await subAdminServcie.getUser(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.subAdmins);
          setTotalPages(res?.data?.data?.pagination?.totalPages);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const deleteData = async (ids) => {
    const isMultiple = Array.isArray(ids);
    Swal.fire({
      text: isMultiple 
        ? `Are you sure you want to delete ${ids.length} selected sub admin(s)?`
        : "Are you sure you want to delete this sub admin?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Keep same key name 'id' but convert to array when multiple
          const payload = isMultiple ? { id: ids } : { id: ids };
          
          const res = await subAdminServcie.deleteUser(payload);
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            if (!isMultiple) {
              setData((prevData) => prevData.filter((item) => item._id !== ids));
              setTotalPages((prevTotal) => prevTotal - 1);
            } else {
              setData((prevData) => prevData.filter((item) => !ids.includes(item._id)));
              setTotalPages((prevTotal) => Math.max(0, prevTotal - ids.length));
            }
            clearSelection();
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedRows.length > 0) {
      deleteData(selectedRows);
    }
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await subAdminServcie.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh]);

  const columns = [
    getCheckboxColumn(),
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: '100px'
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => capitalizeFirstLetter(row.name),
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email,
    },
    {
      name: "Phone Number",
      sortable: true,
      selector: (row) => row?.phoneNumber,
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === 1}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setWarning(
                    `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"
                    } this sub admin?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                setModalShow(true);
                setViewId(row._id);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/subadmin-management/edit/${row?._id}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <IconButton color="primary" onClick={() => deleteData(row._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader title="Sub Admin Management" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />

        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchItem(cleared);
                  debouncedSearch(cleared);
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            startIcon={<DeleteIcon />}
            sx={{ ml: 2 }}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        )}
        <Button
          size="large"
          type="submit"
          variant="contained"
          sx={{ ml: 3, mr: 3 }}
          onClick={() => navigate("/subadmin-management/add")}
        >
          Add
        </Button>
      </Box>
      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) =>
            setPageSize(currentRowsPerPage)
          }
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          paginationComponent={(props) => (
            <CustomPagination {...props} currentPage={currentPage} />
          )}
          className="custom"
        />
      </CardContent>
      <Dialog open={show} onClose={() => setShow(false)} className="popupbox-statis">
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button variant="outlined" onClick={() => setShow(false)} color="primary">
            No
          </Button>
        </DialogActions>
      </Dialog>
      {modalShow ? (
        <ViewUserManagement
          show={modalShow}
          onHide={() => setModalShow(false)}
          id={viewId}
        />
      ) : (
        ""
      )}
    </Card>
  );
}